import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Typography from '@mui/material/Typography/Typography';
import ButtonBase from '@mui/material/ButtonBase/ButtonBase';
import Drawer from '@mui/material/Drawer/Drawer';
import IconButton from '@mui/material/IconButton/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { Guide, guidePosition, sectionsOf } from '../../utils/guides';

const GuideList = ({ guides, current, onNavigate }: { guides: Guide[], current: string, onNavigate?: () => void }) => (
    <Stack component="nav" aria-label="All guides" spacing={2}>
        {sectionsOf(guides).map(section => (
            <Box key={section.audience}>
                <Typography variant="overline" color="text.secondary" component="h2" sx={{ display: 'block', lineHeight: 2 }}>{section.audience}</Typography>
                <Stack component="ul" spacing={0.25} sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    {section.guides.map(guide => {
                        const active = guide.link === current;
                        return (
                            <li key={guide.link}>
                                <Box component={NavLink} to={`/how/to/${guide.link}`} onClick={onNavigate} aria-current={active ? 'page' : undefined}
                                    sx={{
                                        display: 'block', px: 1.5, py: 0.75, borderRadius: 999, fontSize: '0.9375rem', textDecoration: 'none',
                                        // the current guide is a filled ink pill (never a stripe down the side)
                                        color: active ? 'background.paper' : 'text.primary', fontWeight: active ? 600 : 400,
                                        backgroundColor: active ? 'text.primary' : 'transparent',
                                        '&:hover': { backgroundColor: active ? 'text.primary' : 'action.hover' }
                                    }}>
                                    {guide.title}
                                </Box>
                            </li>
                        );
                    })}
                </Stack>
            </Box>
        ))}
        <Box component={Link} to="/how/to" onClick={onNavigate} sx={{ fontSize: '0.875rem', px: 1.25 }}>All guides on one page</Box>
    </Stack>
);

/** Wide screens: a sticky list of every guide beside the article. */
export const GuideSidebar = ({ guides, current }: { guides: Guide[], current: string }) => (
    <Box sx={{ display: { xs: 'none', md: 'block' }, width: 250, flexShrink: 0, position: 'sticky', top: 96, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
        <GuideList guides={guides} current={current} />
    </Box>
);

/** Phones: a bar pinned under the header; its menu button slides the same list in from the side. */
export const GuideMenu = ({ guides, current }: { guides: Guide[], current: string }) => {
    const [open, setOpen] = React.useState(false);
    const title = guides.find(g => g.link === current)?.title;
    return (
        <>
            <Stack direction="row" alignItems="center" spacing={1} sx={{
                display: { xs: 'flex', md: 'none' }, position: 'sticky', top: 64, zIndex: 2, mx: -2, px: 1, py: 0.5,
                backgroundColor: 'background.paper', borderBottom: '1px solid var(--border-color)'
            }}>
                <IconButton aria-label="Open the list of guides" aria-expanded={open} onClick={() => setOpen(true)}><MenuIcon /></IconButton>
                <Typography noWrap sx={{ fontWeight: 600, minWidth: 0 }}>{title ?? 'Guides'}</Typography>
            </Stack>
            <Drawer anchor="left" open={open} onClose={() => setOpen(false)} sx={{ display: { md: 'none' } }}
                PaperProps={{ sx: { width: 300, maxWidth: '85vw', p: 2 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>Guides</Typography>
                    <IconButton aria-label="Close the list of guides" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
                </Stack>
                <GuideList guides={guides} current={current} onNavigate={() => setOpen(false)} />
            </Drawer>
        </>
    );
};

/** "Participants · 2 of 3": where this guide sits, above its title. */
export const GuidePlace = ({ guides, current }: { guides: Guide[], current: string }) => {
    const place = guidePosition(guides, current);
    if (!place || place.count < 2) {
        return place ? <Typography variant="overline" color="text.secondary">{place.section}</Typography> : null;
    }
    return <Typography variant="overline" color="text.secondary">{place.section} · {place.index + 1} of {place.count}</Typography>;
};

const StepCard = ({ guide, direction, label }: { guide: Guide, direction: 'prev' | 'next', label: string }) => (
    <ButtonBase component={Link} to={`/how/to/${guide.link}`} aria-label={`${direction === 'prev' ? 'Previous' : 'Next'} guide: ${guide.title}`}
        sx={{
            flex: 1, minWidth: 200, p: 2, borderRadius: '12px', border: '1px solid var(--border-color)',
            display: 'flex', alignItems: 'center', gap: 1.5, textAlign: direction === 'prev' ? 'left' : 'right',
            justifyContent: direction === 'prev' ? 'flex-start' : 'flex-end', color: 'text.primary', textDecoration: 'none',
            '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' }
        }}>
        {direction === 'prev' && <ArrowBackIcon color="primary" />}
        <Box>
            <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
            <Typography sx={{ fontWeight: 600 }}>{guide.title}</Typography>
        </Box>
        {direction === 'next' && <ArrowForwardIcon color="primary" />}
    </ButtonBase>
);

/** Previous / next at the end of a guide, so the next step is one click away instead of back at the index. */
export const GuidePager = ({ guides, current }: { guides: Guide[], current: string }) => {
    const place = guidePosition(guides, current);
    if (!place || (!place.prev && !place.next)) {
        return null;
    }
    // stepping out of the section says where you're going
    const label = (guide: Guide, word: string) => guide.audience === place.section ? word : `${word} in ${guide.audience}`;
    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 2, borderTop: '1px solid var(--border-color)' }}>
            {place.prev ? <StepCard guide={place.prev} direction="prev" label={label(place.prev, 'Previous')} /> : <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />}
            {place.next ? <StepCard guide={place.next} direction="next" label={label(place.next, 'Next')} /> : <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />}
        </Stack>
    );
};
